import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';

type ProviderStatus = 'idle' | 'triggering' | 'success' | 'error';

type ProviderState = {
  status: ProviderStatus;
  message?: string;
};

export const CatalogProviderTrigger = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [providerStates, setProviderStates] = useState<
    Record<string, ProviderState>
  >({});

  const {
    value: providers,
    loading,
    error: listError,
  } = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('ai-provider-trigger');
    const { token } = await identityApi.getCredentials();
    const res = await fetch(`${baseUrl}/providers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      throw new Error(
        `Failed to list providers: ${res.status} ${res.statusText}`,
      );
    }
    const data = await res.json();
    return data.providers as string[];
  }, []);

  const triggerProvider = useCallback(
    async (providerName: string) => {
      setProviderStates(prev => ({
        ...prev,
        [providerName]: { status: 'triggering' },
      }));
      try {
        const baseUrl = await discoveryApi.getBaseUrl('ai-provider-trigger');
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${baseUrl}/trigger/${encodeURIComponent(providerName)}`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        setProviderStates(prev => ({
          ...prev,
          [providerName]: {
            status: 'success',
            message: 'Refresh triggered — running in background.',
          },
        }));
      } catch (err: any) {
        setProviderStates(prev => ({
          ...prev,
          [providerName]: {
            status: 'error',
            message: err?.message ?? 'Unknown error',
          },
        }));
      }
    },
    [discoveryApi, identityApi],
  );

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8 }} p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading providers…</Typography>
      </Box>
    );
  }

  if (listError) {
    return (
      <Alert severity="error">
        Failed to load providers: {listError.message}
      </Alert>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Catalog Provider Trigger
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 16 }}
      >
        Manually trigger a full refresh for any registered entity provider.
        Refreshes run in the background — check the backend logs for progress.
      </Typography>

      {!providers?.length ? (
        <Alert severity="info">No providers are currently registered.</Alert>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {providers.map((name, idx) => {
              const state = providerStates[name] ?? { status: 'idle' };
              const isTriggering = state.status === 'triggering';
              return (
                <ListItem
                  key={name}
                  divider={idx < providers.length - 1}
                  style={{ paddingRight: 120 }}
                >
                  <ListItemText
                    primary={name}
                    secondary={(() => {
                      if (state.status === 'success') {
                        return (
                          <span style={{ color: 'green' }}>
                            {state.message}
                          </span>
                        );
                      }
                      if (state.status === 'error') {
                        return (
                          <span style={{ color: 'red' }}>{state.message}</span>
                        );
                      }
                      return null;
                    })()}
                  />
                  <ListItemSecondaryAction>
                    {state.status === 'success' ? (
                      <Chip
                        label="Triggered"
                        size="small"
                        style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                      />
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={isTriggering}
                        onClick={() => triggerProvider(name)}
                        startIcon={
                          isTriggering ? (
                            <CircularProgress size={14} />
                          ) : undefined
                        }
                      >
                        {isTriggering ? 'Triggering…' : 'Trigger'}
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
};
